import { contactFormSchema } from './yupSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FC, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { baseUrl } from '@/src/assets/utils/baseUrl';

import Button from '../../UI/button/Button';
import { Title } from '../../UI/title/Title';
import Wrapper from '../../UI/wrapper/Wrapper';
import Breadcrumbs from '../../breadcrumbs/Breadcrumbs';
import { ErrorIcon } from '../../icons/errorIcon/ErrorIcon';
import { LoaderIcon } from '../../icons/loaderIcon/LoaderIcon';
import { SuccessIcon } from '../../icons/successIcon/SuccessIcon';
import Layout from '../../layout/Layout';

import styles from './Contacts.module.scss';

interface Inputs {
    name: string;
    email: string;
    message: string;
}

interface ContactFormState {
    isLoading: boolean;
    submitted: boolean;
    isEmailSent: boolean;
}

const Contacts: FC = () => {
    const [contactFormState, setContactFormState] = useState<ContactFormState>({
        isLoading: false,
        submitted: false,
        isEmailSent: false,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<Inputs>({
        mode: 'all',
        resolver: yupResolver(contactFormSchema),
    });

    const postData = async (data: Inputs) => {
        setContactFormState((prev) => {
            return {
                ...prev,
                isLoading: true,
            };
        });

        const response = await fetch('/api/contact', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        });

        setContactFormState(() => {
            return {
                submitted: true,
                isEmailSent: response.ok,
                isLoading: false,
            };
        });

        reset();
    };

    const onSubmit: SubmitHandler<Inputs> = (data) => {
        postData(data);
    };

    const jsonLd = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: 'High Five Themes Support',
        description:
            'Harness the ethereal power of this platform to captivate your audience and unleash the full potential of your content.',
        url: `${baseUrl('contacts')}`,
        potentialAction: {
            '@type': 'RegisterAction',
            contactType: 'contact form',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${baseUrl('contacts')}`,
                httpMethod: 'POST',
            },
        },
    };

    const isValidationErrors = !!Object.keys(errors).length;

    const heading = 'Feel Free to Contact Us';
    const excerpt = `Your feedback and questions are essential to us. Fill out the form below, and our team
    will get back to you as soon as possible.`;

    return (
        <Layout
            meta={{
                metaTitle: 'Contact | High Five Themes',
                metaDescription:
                    'Your feedback matters. Reach out to us on our Contact page to share your thoughts or get help with our Ghost themes.',
                ogTitle: 'Contact | High Five Themes',
                ogDescription:
                    'Your feedback matters. Reach out to us on our Contact page to share your thoughts or get help with our Ghost themes.',
            }}
            jsonLD={jsonLd}>
            <Wrapper className={styles.wrapper}>
                <Breadcrumbs />
                <div className={styles.info}>
                    <Title text={heading} size="xl" className={styles.heading} />
                    <p className={`excerpt body-lg excerpt-wrap ${styles.excerpt}`}>{excerpt}</p>
                </div>
                {contactFormState.submitted ? (
                    <div className={styles.formSubmissionResult}>
                        {contactFormState.isEmailSent ? <SuccessIcon /> : <ErrorIcon />}

                        <Title
                            text={
                                contactFormState.isEmailSent
                                    ? 'Success! Your message has been sent!'
                                    : 'Oops! Something went wrong!'
                            }
                            size="lg"
                            className={styles.formSubmissionResult__title}
                        />

                        <p className={styles.formSubmissionResult__desc}>
                            {contactFormState.isEmailSent
                                ? 'Thank you! We will get to you as soon as possible.'
                                : 'There was an error trying to send your message. Please try again later.'}
                        </p>
                        <Button buttonUrl={'/'} ariaLabel="Back to home page link" color="standard">
                            Ok, Thanks!
                        </Button>
                    </div>
                ) : (
                    <form
                        action=""
                        className={styles.form}
                        onSubmit={handleSubmit(onSubmit)}
                        noValidate>
                        <Title
                            text="Ready To Get Started?"
                            size="sm"
                            className={styles.form__title}
                        />

                        <div className={styles.inputsContainer}>
                            <div className={styles.inputWrapper}>
                                <label htmlFor="contact-name" className={styles.lable}>
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="contact-name"
                                    placeholder="Name"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.name,
                                    })}
                                    aria-label="Name"
                                    {...register('name', { required: true })}
                                />
                                {errors.name && (
                                    <p className={styles.error}>{errors.name.message}</p>
                                )}
                            </div>
                            <div className={styles.inputWrapper}>
                                <label htmlFor="contact-email" className={styles.lable}>
                                    E-mail
                                </label>
                                <input
                                    type="email"
                                    id="contact-email"
                                    placeholder="E-mail"
                                    className={classNames(styles.input, {
                                        [styles['input--error']]: errors.email,
                                    })}
                                    aria-label="E-mail"
                                    {...register('email', { required: true })}
                                />
                                {errors.email && (
                                    <p className={styles.error}>{errors.email.message}</p>
                                )}
                            </div>
                            <div className={styles.area__wrapper}>
                                <label htmlFor="contact-area" className={styles.lable}>
                                    Message
                                </label>
                                <textarea
                                    id="contact-area"
                                    placeholder="Type Something"
                                    className={classNames(styles.area, {
                                        [styles['area--error']]: errors.message,
                                    })}
                                    aria-label="Text area for your message"
                                    {...register('message', { required: true })}
                                />
                                {errors.message && (
                                    <p className={styles.error}>{errors.message.message}</p>
                                )}
                            </div>
                        </div>
                        <button
                            type="submit"
                            aria-label="Submit Request"
                            color="blue"
                            className={classNames(styles.button, {
                                [styles[`button--isLoading`]]: contactFormState.isLoading,
                                [styles[`button--locked`]]: isValidationErrors,
                            })}>
                            {contactFormState.isLoading ? <LoaderIcon /> : 'Submit Request'}
                        </button>
                    </form>
                )}
            </Wrapper>
        </Layout>
    );
};

export default Contacts;
